import { isParentRole, isParentUser } from 'src/utils/userRole';
import { isNative } from 'src/utils/platform';
import { env } from 'src/config/env';
import { User, UserType } from 'src/types/user.types';
import { getModuleExternalId } from 'src/utils/external-id';
import { getAnalyticsDistinctId, logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { reportError } from 'src/modules/logs/Sentry';
import type { AFInit } from 'appsflyer-capacitor-plugin';
import { AppsFlyer, AFConstants } from 'appsflyer-capacitor-plugin';

let isInitialized = false;

let isTrackingEnabled = true;

let initPromise: Promise<void> | null = null;

let isStarted = false;

let startPromise: Promise<void> | null = null;

let resolvedUid = '';

const uidListeners = new Set<(uid: string) => void>();

const notifyUidResolved = (uid: string) => {
  if (!uid) return;
  resolvedUid = uid;
  uidListeners.forEach((listener) => {
    try {
      listener(uid);
    } catch (error) {
      reportError('AppsFlyer UID listener failed', error);
    }
  });
};

export const onAppsFlyerUidResolved = (listener: (uid: string) => void): (() => void) => {
  uidListeners.add(listener);
  if (resolvedUid) {
    try {
      listener(resolvedUid);
    } catch (error) {
      reportError('AppsFlyer UID listener failed', error);
    }
  }
  return () => uidListeners.delete(listener);
};

export const __resetUidListenersForTest = (): void => {
  uidListeners.clear();
  resolvedUid = '';
};

let lastPrivacyUserType: UserType | undefined | null = null;

const configurePrivacy = async (userType?: UserType) => {
  if (userType === lastPrivacyUserType) {
    return;
  }
  lastPrivacyUserType = userType;

  if (!isParentRole(userType)) {
    await AppsFlyer.anonymizeUser({ anonymizeUser: true });
    await AppsFlyer.disableAdvertisingIdentifier({ shouldDisable: true });
    await AppsFlyer.setSharingFilterForPartners({ filters: ['all'] });
  } else {
    await AppsFlyer.anonymizeUser({ anonymizeUser: false });
    await AppsFlyer.disableAdvertisingIdentifier({ shouldDisable: false });
    await AppsFlyer.setSharingFilterForPartners({ filters: [] });
  }
};

// How long we wait for the native start callback before giving up on it. The plugin has no timeout on
// iOS: it settles only from start()'s completion handler, so a pending call is never settled later.
export const START_TIMEOUT_MS = 15_000;

// iOS rejects a start that comes within minTimeBetweenSessions of the last launch, yet the SDK runs.
const isSessionGapRejection = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('Event timeout') && message.includes('minTimeBetweenSessions');
};

// Initialise the AppsFlyer SDK WITHOUT starting it.
export const initAppsFlyerSdk = async (): Promise<void> => {
  if (!isNative()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const devKey = env.appsFlyerDevKey;
      const appId = env.appsFlyerAppId;
      if (!devKey || !appId) {
        console.warn(
          'AppsFlyer SDK not initialized: missing VITE_APPSFLYER_DEV_KEY or VITE_APPSFLYER_APP_ID',
        );
        return;
      }

      const afConfig: AFInit = {
        appID: appId,
        devKey,
        isDebug: env.isDev,
        minTimeBetweenSessions: 60,
        registerOnDeepLink: true,
        registerConversionListener: false,
        registerOnAppOpenAttribution: false,
        manualStart: true,
      };
      // waitForATTUserAuthorization is deliberately ABSENT: passing 0 is not a no-op, the plugin then
      // enters its undocumented ATT-timer path. ATT is resolved before startSDK().
      await AppsFlyer.initSDK(afConfig);
      isInitialized = true;
    } catch (error) {
      initPromise = null;
      reportError('Error initializing AppsFlyer SDK', error);
    }
  })();

  return initPromise;
};

// Start the SDK — the network-bound half.
export const startAppsFlyerSdk = (): void => {
  if (!isNative() || startPromise) return;

  const startedAt = Date.now();

  startPromise = (async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        AppsFlyer.startSDK().catch((error: unknown) => {
          if (!isSessionGapRejection(error)) throw error;
        }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`AppsFlyer start timed out after ${START_TIMEOUT_MS}ms`)),
            START_TIMEOUT_MS,
          );
        }),
      ]);
      isStarted = true;

      let uid = '';
      try {
        uid = (await AppsFlyer.getAppsFlyerUID()).uid;
      } catch (error) {
        reportError('Error getting AppsFlyer UID', error);
      }
      notifyUidResolved(uid);
      logAnalyticsEvent('appsflyer_uid_resolved', {
        isSuccess: uid !== '',
        durationMs: Date.now() - startedAt,
      });
    } catch (error) {
      reportError('AppsFlyer start failed', error);
      logAnalyticsEvent('appsflyer_uid_resolved', {
        isSuccess: false,
        durationMs: Date.now() - startedAt,
      });
    } finally {
      clearTimeout(timer);
    }
  })();
};

const waitForInit = async (): Promise<boolean> => {
  if (!isNative()) {
    return false;
  }
  // Self-heal. A bare `if (!isInitialized) return false` here meant a caller
  // arriving before init silently degraded — loginAppsFlyer returned {uid: ''} and RevenueCat got
  // an empty $appsflyerId, losing the revenue/attribution join.
  await initAppsFlyerSdk();
  return isInitialized;
};

export const initAppsFlyer = async (userType?: UserType) => {
  if (!isNative()) {
    return;
  }

  await initAppsFlyerSdk();
  if (!isInitialized) return;

  await configurePrivacy(userType);

  isTrackingEnabled = isParentRole(userType);
};

export const __getInitPromiseForTest = (): Promise<void> | null => initPromise;

export const __isInitializedForTest = (): boolean => isInitialized;

export const __isStartedForTest = (): boolean => isStarted;

export const loginAppsFlyer = async (user?: User) => {
  const ready = await waitForInit();
  if (!ready) {
    return { uid: '' };
  }

  if (!user) {
    await AppsFlyer.setCustomerUserId({ cuid: '' });
    await AppsFlyer.setAdditionalData({ additionalData: {} });
    const result = await AppsFlyer.getAppsFlyerUID();
    notifyUidResolved(result.uid);
    return result;
  }

  const userId = getModuleExternalId(user.id);
  await AppsFlyer.setCustomerUserId({ cuid: userId });

  const analyticsDistinctId = getAnalyticsDistinctId();
  const additionalData: Record<string, string> = {
    family_id: String(user.family_id),
    user_type: user.user_type ?? '',
  };

  if (analyticsDistinctId) {
    additionalData.posthog_distinct_id = analyticsDistinctId;
  }

  await AppsFlyer.setAdditionalData({ additionalData });

  if (user.email && isParentUser(user)) {
    await AppsFlyer.setUserEmails({
      emails: [user.email.toLowerCase().trim()],
      encode: true,
    });
  }

  const result = await AppsFlyer.getAppsFlyerUID();
  notifyUidResolved(result.uid);
  return result;
};

export const getAppsFlyerUID = async (): Promise<string> => {
  if (!isNative() || !isInitialized) {
    return '';
  }

  try {
    return (await AppsFlyer.getAppsFlyerUID()).uid;
  } catch (error) {
    reportError('Error getting AppsFlyer UID', error);
    return '';
  }
};

export const logAppsFlyerEvent = async (
  eventName: string,
  eventValue?: Record<string, unknown>,
) => {
  if (!isNative() || !isTrackingEnabled) return;

  try {
    await AppsFlyer.logEvent({
      eventName,
      eventValue: eventValue ?? {},
    });
  } catch (error) {
    reportError('Error logging AppsFlyer event', error);
  }
};

type DeepLinkHandler = (url: string) => void;

let deepLinkHandler: DeepLinkHandler | null = null;
let pendingDeepLink: string | null = null;

// Register the AppsFlyer UDL listener.
export const registerAppsFlyerDeepLinkListener = () => {
  if (!isNative()) {
    return;
  }

  AppsFlyer.addListener(AFConstants.UDL_CALLBACK, res => {
    if (res.status === 'FOUND') {
      const deepLinkValue =
        res.deepLink?.deep_link_value ?? res.deepLink?.link;
      if (deepLinkValue) {
        if (deepLinkHandler) {
          deepLinkHandler(deepLinkValue);
        } else {
          pendingDeepLink = deepLinkValue;
        }
      }
    }
  });

  AppsFlyer.setAppInviteOneLink({ onelinkID: 'oQf0' });
};

export const setDeepLinkHandler = (handler: DeepLinkHandler) => {
  deepLinkHandler = handler;
  if (pendingDeepLink) {
    handler(pendingDeepLink);
    pendingDeepLink = null;
  }
};

export const APPSFLYER_EVENT_MAP: Record<string, string> = {
  signup: 'af_complete_registration',
  trial_started: 'af_start_trial',
  onboarding_complete: 'af_tutorial_completion',
  purchase: 'af_purchase',
};

export const APPSFLYER_EVENTS = [
  'signup',
  'onboarding_complete',
  'trial_started',
  'purchase',
];
